package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.CalendarFeed
import com.evoapps.evoshift.domain.model.DayRange
import com.evoapps.evoshift.domain.model.PrintedRoster
import com.evoapps.evoshift.domain.model.RosterMailRefusal
import com.evoapps.evoshift.domain.model.RosterMailRefusedException
import com.evoapps.evoshift.domain.model.RosterMailDelivery
import com.evoapps.evoshift.domain.model.RosterMailResult
import com.evoapps.evoshift.domain.repository.ScheduleDistributionRepository
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.add
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

/** The sendRosterEmail and calendarFeed callables (functions/index.js, SH-45). */
class ScheduleDistributionRepositoryImpl(private val functions: CloudFunctionsClient) : ScheduleDistributionRepository {

    override suspend fun emailRoster(rosterId: String, userId: String?, days: DayRange?): Result<RosterMailResult> = try {
        val result = functions.call("sendRosterEmail", buildJsonObject {
            put("rosterId", rosterId)
            if (userId != null) put("userId", userId)
            if (days != null) {
                put("fromDay", days.fromDay)
                put("toDay", days.toDay)
            }
        })
        Result.success(parseMailResult(result))
    } catch (e: CloudFunctionsException) {
        Result.failure(refusal(e)?.let(::RosterMailRefusedException) ?: e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    override suspend fun rosterMailDelivery(mailIds: List<String>): Result<RosterMailDelivery> = runCatching {
        parseDelivery(functions.call("rosterMailStatus", buildJsonObject {
            put("mailIds", buildJsonArray { mailIds.forEach { add(it) } })
        }))
    }

    override suspend fun printRoster(rosterId: String, days: DayRange?, language: String): Result<PrintedRoster> = runCatching {
        parsePrinted(functions.call("printRoster", buildJsonObject {
            put("rosterId", rosterId)
            put("language", language)
            if (days != null) {
                put("fromDay", days.fromDay)
                put("toDay", days.toDay)
            }
        }))
    }

    override suspend fun calendarFeed() = feed("status")
    override suspend fun createCalendarFeed() = feed("create")
    override suspend fun revokeCalendarFeed() = feed("revoke")

    private suspend fun feed(action: String): Result<CalendarFeed> = runCatching {
        parseFeed(functions.call("calendarFeed", buildJsonObject { put("action", action) }))
    }

    companion object {
        /** The refusals sendRosterEmail answers with (its messages are part of the contract). */
        fun refusal(e: CloudFunctionsException): RosterMailRefusal? = when {
            e.code == "resource-exhausted" -> RosterMailRefusal.TOO_SOON
            e.code != "failed-precondition" -> null
            e.message.orEmpty().contains("Publish") -> RosterMailRefusal.NOT_PUBLISHED
            e.message.orEmpty().contains("plan") -> RosterMailRefusal.PLAN_ENDED
            else -> RosterMailRefusal.NOT_SET_UP
        }

        /** sendRosterEmail's answer; servers before SH-242 send only the two counts. */
        fun parseMailResult(result: JsonElement): RosterMailResult {
            val o = result as? JsonObject
            return RosterMailResult(
                queued = o.int("queued"),
                withoutEmail = o.int("withoutEmail"),
                withoutEmailNames = o.strings("withoutEmailNames"),
                mailIds = o.strings("mailIds")
            )
        }

        /** rosterMailStatus's answer (SH-242). */
        fun parseDelivery(result: JsonElement): RosterMailDelivery {
            val o = result as? JsonObject
            return RosterMailDelivery(
                sent = o.int("sent"),
                failed = o.int("failed"),
                pending = o.int("pending"),
                failedNames = o.strings("failedNames")
            )
        }

        private fun JsonObject?.int(key: String): Int = this?.get(key)?.jsonPrimitive?.intOrNull ?: 0

        private fun JsonObject?.strings(key: String): List<String> =
            (this?.get(key) as? JsonArray)?.mapNotNull { (it as? JsonPrimitive)?.contentOrNull?.takeIf(String::isNotBlank) }.orEmpty()

        /** printRoster's answer (SH-261): the PDF's link and file name. */
        fun parsePrinted(result: JsonElement): PrintedRoster {
            val o = result as? JsonObject
            val url = o?.get("url")?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() } ?: error("The printed roster has no link")
            return PrintedRoster(url = url, fileName = o["fileName"]?.jsonPrimitive?.contentOrNull.orEmpty())
        }

        fun parseFeed(result: JsonElement): CalendarFeed {
            val o = result as? JsonObject
            return CalendarFeed(
                active = o?.get("active")?.jsonPrimitive?.booleanOrNull ?: false,
                url = o?.get("url")?.jsonPrimitive?.contentOrNull,
                createdAt = o?.get("createdAt")?.jsonPrimitive?.contentOrNull
            )
        }
    }
}
